/**
 * Scheduler Page Component
 *
 * Main scheduler interface for managing guard patrol assignments.
 * Features:
 * - Calendar view of weekly schedules (left panel)
 * - Shift view for detailed shift inspection (right panel)
 * - Independent views: Calendar and ShiftView operate independently
 * - Views sync only when: clicking patrol in Calendar, or database writes
 * - Guard and company/location view modes for Calendar
 * - Shift grouping and visualization
 * - Patrol creation, editing, and deletion
 * - Duplicate shift/week functionality
 * - Conflict detection for double-bookings
 * - Drag-and-drop patrol reordering
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Backdrop,
  Fade,
  Fab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import ControlBar from 'containers/pages/scheduler/ControlBar';
import SchedulerCalendar from 'containers/pages/scheduler/Calendar';
import ShiftView from 'containers/pages/scheduler/ShiftView/index';
import PatrolModal from 'containers/pages/scheduler/PatrolModal/index';
import ExportModal from 'containers/pages/scheduler/ExportModal/index';
import MobileShiftDrawer from 'containers/pages/scheduler/MobileShiftDrawer';
import useSchedulerApi from 'containers/pages/scheduler/hooks/useSchedulerApi';
import useDebounce from 'containers/pages/scheduler/hooks/useDebounce';
import updateSinglePatrol from 'api/scheduler/updateSinglePatrol';
import { calculateShifts, findShiftForPatrol } from 'utils/shiftCalculator';
import { getWeekStart, getWeekEnd } from 'utils/dateUtils';
import { SCHEDULE_LOAD_DEBOUNCE_MS } from 'containers/pages/scheduler/constants';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { useSearchParams } from 'react-router-dom';

const Scheduler = () => {
  // =====================
  // RESPONSIVE BREAKPOINTS
  // =====================
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-900px

  // URL search params (for navigation from other pages with autofill)
  const [searchParams, setSearchParams] = useSearchParams();

  // Mobile drawer state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // =====================
  // CALENDAR STATE
  // =====================
  // View mode for Calendar (company view or guard view)
  const [viewMode, setViewMode] = useState('guard'); // 'company' | 'guard'

  // Calendar filters (company/location view)
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  // Calendar filters (guard view)
  const [selectedGuard, setSelectedGuard] = useState('');
  const [selectedGuardId, setSelectedGuardId] = useState(null);

  // Calendar week tracking
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => getWeekStart());

  // Calendar patrols (loaded based on Calendar's selection)
  const [calendarPatrols, setCalendarPatrols] = useState([]);

  // =====================
  // SHIFTVIEW STATE (Independent)
  // =====================
  // ShiftView guard and week - independent of Calendar
  const [shiftViewGuard, setShiftViewGuard] = useState('');
  const [shiftViewGuardId, setShiftViewGuardId] = useState(null);
  const [shiftViewWeekStart, setShiftViewWeekStart] = useState(() => getWeekStart());

  // ShiftView patrols (loaded based on ShiftView's guard/week)
  const [shiftViewPatrols, setShiftViewPatrols] = useState([]);

  // Currently selected shift in ShiftView
  const [selectedShift, setSelectedShift] = useState(null);

  // =====================
  // SHARED STATE
  // =====================
  const [guards, setGuards] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPatrol, setSelectedPatrol] = useState(null);
  const [autofillData, setAutofillData] = useState({
    CustomerName: '',
    AddressName: '',
    ContactId: null,
    ContactName: '',
    date: '',
    startTime: '',
    duration: '',
  });

  // Ref to hold the calendar navigation function
  const calendarNavigateRef = useRef(null);

  // Refs to hold API functions to avoid useEffect re-runs
  const loadScheduleByContactRef = useRef(null);
  const loadScheduleByCompanyRef = useRef(null);

  // API hook with centralized error handling and granular loading states
  const {
    loadInitialData,
    loadScheduleByContact,
    loadScheduleByCompany,
    savePatrol,
    deletePatrol,
    bulkSavePatrols,
    checkBulkConflicts,
    deleteShift,
    changeShiftGuard,
    notifyError,
    notifySuccess,
    loadingState,
  } = useSchedulerApi();

  const { notifyInfo } = useNotificationProvider();

  // Bulk operation conflict state
  const [showBulkConflictWarning, setShowBulkConflictWarning] = useState(false);
  const [bulkConflictSummary, setBulkConflictSummary] = useState(null);
  const [pendingBulkOperation, setPendingBulkOperation] = useState(null);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  // Debounced selection values for Calendar schedule loading
  // This prevents rapid API calls when selection changes quickly
  const debouncedCalendarGuardId = useDebounce(selectedGuardId, SCHEDULE_LOAD_DEBOUNCE_MS);
  const debouncedCalendarCompanyId = useDebounce(selectedCompanyId, SCHEDULE_LOAD_DEBOUNCE_MS);
  const debouncedCalendarLocationId = useDebounce(selectedLocationId, SCHEDULE_LOAD_DEBOUNCE_MS);

  // Debounced selection values for ShiftView schedule loading
  const debouncedShiftViewGuardId = useDebounce(shiftViewGuardId, SCHEDULE_LOAD_DEBOUNCE_MS);

  // Keep API function refs current to avoid effect dependency issues
  loadScheduleByContactRef.current = loadScheduleByContact;
  loadScheduleByCompanyRef.current = loadScheduleByCompany;

  // Handle view mode change - clear Calendar selections and patrols when switching
  const handleViewModeChange = useCallback((newMode) => {
    setViewMode(newMode);
    setCalendarPatrols([]); // Clear Calendar patrols when switching view modes
    if (newMode === 'company') {
      setSelectedGuard('');
      setSelectedGuardId(null);
    } else {
      setSelectedCompany('');
      setSelectedCompanyId(null);
      setSelectedLocation('');
      setSelectedLocationId(null);
    }
    // Note: Don't clear ShiftView state - it operates independently
  }, []);

  // Fetch guards and companies on component mount
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const { guards: guardsData, companies: companiesData } = await loadInitialData();
        if (isMounted) {
          setGuards(guardsData);
          setCompanies(companiesData);
          setInitialLoadComplete(true);
        }
      } catch (error) {
        // Error already logged and user notified by useSchedulerApi
        console.error('[Scheduler] Initial data load failed:', error);
        if (isMounted) {
          setInitialLoadComplete(true); // Mark as complete even on error to show UI
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [loadInitialData]);

  // =====================
  // CALENDAR DATA LOADING
  // =====================
  // Auto-load Calendar schedules when Calendar selection or week changes
  useEffect(() => {
    const hasGuardSelection = viewMode === 'guard' && debouncedCalendarGuardId;
    const hasCompanySelection = viewMode === 'company' && debouncedCalendarCompanyId;

    if (!calendarWeekStart || (!hasGuardSelection && !hasCompanySelection)) {
      return;
    }

    let isMounted = true;

    const loadCalendarPatrols = async () => {
      try {
        const weekEnd = getWeekEnd(calendarWeekStart);
        let patrolsData;

        if (hasGuardSelection) {
          patrolsData = await loadScheduleByContactRef.current(debouncedCalendarGuardId, calendarWeekStart, weekEnd);
        } else {
          patrolsData = await loadScheduleByCompanyRef.current(
            debouncedCalendarCompanyId,
            calendarWeekStart,
            weekEnd,
            debouncedCalendarLocationId || null
          );
        }

        if (isMounted) {
          setCalendarPatrols(patrolsData);
        }
      } catch (error) {
        console.error('[Scheduler] Failed to load Calendar patrols:', error);
        if (isMounted) {
          setCalendarPatrols([]);
        }
      }
    };

    loadCalendarPatrols();

    return () => {
      isMounted = false;
    };
  }, [viewMode, debouncedCalendarGuardId, debouncedCalendarCompanyId, debouncedCalendarLocationId, calendarWeekStart]);

  // =====================
  // SHIFTVIEW DATA LOADING
  // =====================
  // Auto-load ShiftView schedules when ShiftView guard or week changes
  useEffect(() => {
    if (!shiftViewWeekStart || !debouncedShiftViewGuardId) {
      return;
    }

    let isMounted = true;

    const loadShiftViewPatrols = async () => {
      try {
        const weekEnd = getWeekEnd(shiftViewWeekStart);
        const patrolsData = await loadScheduleByContactRef.current(debouncedShiftViewGuardId, shiftViewWeekStart, weekEnd);

        if (isMounted) {
          setShiftViewPatrols(patrolsData);
        }
      } catch (error) {
        console.error('[Scheduler] Failed to load ShiftView patrols:', error);
        if (isMounted) {
          setShiftViewPatrols([]);
        }
      }
    };

    loadShiftViewPatrols();

    return () => {
      isMounted = false;
    };
  }, [debouncedShiftViewGuardId, shiftViewWeekStart]);

  // Handle guard selection change - receives ID directly from ControlBar
  const handleGuardChange = useCallback(
    (guardId) => {
      setSelectedGuardId(guardId || null);
      const guard = guards.find((g) => g.id === guardId);
      setSelectedGuard(guard?.name || '');
    },
    [guards]
  );

  // Handle company selection change - track company ID for API calls
  const handleCompanyChange = useCallback(
    (companyName, companyId) => {
      setSelectedCompany(companyName);
      setSelectedCompanyId(companyId || null);
      // Clear location when company changes
      setSelectedLocation('');
      setSelectedLocationId(null);
    },
    []
  );

  // Handle location selection change - track location ID for API calls
  const handleLocationChange = useCallback((locationName, locationId) => {
    setSelectedLocation(locationName);
    setSelectedLocationId(locationId || null);
  }, []);

  // Handle Calendar week change from calendar navigation
  const handleCalendarWeekChange = useCallback((newDate) => {
    setCalendarWeekStart((prev) => {
      const newWeekStart = getWeekStart(newDate);
      // Only update if the week actually changed (compare timestamps)
      // This prevents unnecessary re-renders and API calls
      if (prev && prev.getTime() === newWeekStart.getTime()) {
        return prev;
      }
      return newWeekStart;
    });
  }, []);

  // Handle ShiftView's guard change - INDEPENDENT of Calendar
  // Receives ID and name from ShiftBrowser
  const handleShiftViewGuardChange = useCallback((guardId, guardName) => {
    setShiftViewGuardId(guardId || null);
    if (guardName !== undefined) {
      setShiftViewGuard(guardName);
    } else {
      const guard = guards.find((g) => g.id === guardId);
      setShiftViewGuard(guard?.name || '');
    }
    // Clear selected shift when changing guards
    setSelectedShift(null);
  }, [guards]);

  // Handle ShiftView's week change - INDEPENDENT of Calendar
  const handleShiftViewWeekChange = useCallback((newDate) => {
    setShiftViewWeekStart((prev) => {
      const newWeekStart = getWeekStart(newDate);
      if (prev && prev.getTime() === newWeekStart.getTime()) {
        return prev;
      }
      return newWeekStart;
    });
  }, []);

  // Handle "Sync to Calendar" button - syncs Calendar to match ShiftView's selection
  const handleSyncToCalendar = useCallback(() => {
    if (!shiftViewGuardId) return;

    // Switch Calendar to guard view and set the same guard
    setViewMode('guard');
    setSelectedGuardId(shiftViewGuardId);
    setSelectedGuard(shiftViewGuard);

    // Clear company view state
    setSelectedCompany('');
    setSelectedCompanyId(null);
    setSelectedLocation('');
    setSelectedLocationId(null);

    // Sync the week
    setCalendarWeekStart(shiftViewWeekStart);

    // Navigate Calendar to the same week
    if (calendarNavigateRef.current && shiftViewWeekStart) {
      calendarNavigateRef.current(shiftViewWeekStart);
    }

    notifyInfo(`Calendar synced to ${shiftViewGuard}`);
  }, [shiftViewGuard, shiftViewGuardId, shiftViewWeekStart, notifyInfo]);

  // Calculate shifts for Calendar (memoized)
  const calendarShifts = useMemo(() => calculateShifts(calendarPatrols), [calendarPatrols]);

  // Calculate shifts for ShiftView (memoized)
  const shiftViewShifts = useMemo(() => calculateShifts(shiftViewPatrols), [shiftViewPatrols]);

  // Keep selectedShift in sync with recalculated ShiftView shifts (e.g., after reordering)
  useEffect(() => {
    if (selectedShift) {
      // Find the updated shift with the same guard and overlapping patrols
      const updatedShift = shiftViewShifts.find(
        (s) =>
          String(s.ContactId) === String(selectedShift.ContactId) &&
          s.patrols.some((p) => selectedShift.patrols.some((sp) => sp.ScheduleAssignmentId === p.ScheduleAssignmentId))
      );
      if (updatedShift && updatedShift !== selectedShift) {
        setSelectedShift(updatedShift);
      }
    }
  }, [shiftViewShifts, selectedShift]);

  // Handle clicking patrol in PatrolList or Calendar edit button - opens modal
  const handlePatrolClick = useCallback((patrol) => {
    setSelectedPatrol(patrol);
    setModalOpen(true);
  }, []);

  // Handle clicking event in Calendar - loads guard data into ShiftView and selects shift
  // This is a SYNC POINT: clicking patrol in Calendar updates ShiftView
  const handleCalendarEventClick = useCallback(
    (contactName, date) => {
      // Find the patrol that was clicked based on guard name and start time
      const clickedPatrol = calendarPatrols.find(
        (p) =>
          p.ContactName === contactName &&
          new Date(p.StartTime).getTime() === new Date(date).getTime()
      );

      if (clickedPatrol) {
        // Update ShiftView to show this guard's data
        setShiftViewGuardId(clickedPatrol.ContactId);
        setShiftViewGuard(clickedPatrol.ContactName);

        // Set ShiftView week to match the clicked patrol's week
        const patrolWeekStart = getWeekStart(new Date(clickedPatrol.StartTime));
        setShiftViewWeekStart(patrolWeekStart);

        // Find the shift in Calendar's shifts (for immediate display before ShiftView loads)
        const shift = findShiftForPatrol(calendarShifts, clickedPatrol);
        if (shift) {
          setSelectedShift(shift);
        }

        // Open the mobile drawer when an event is clicked on mobile
        if (isMobile) {
          setMobileDrawerOpen(true);
        }
      }
    },
    [calendarPatrols, calendarShifts, guards, isMobile]
  );

  // Handle opening modal for creating new patrol
  const handleAddNewPatrol = useCallback((autofill = {}) => {
    setSelectedPatrol(null);
    setAutofillData({
      CustomerName: autofill.CustomerName || '',
      AddressName: autofill.AddressName || '',
      ContactId: autofill.ContactId || null,
      ContactName: autofill.ContactName || '',
      date: autofill.date || '',
      startTime: autofill.startTime || '',
      duration: autofill.duration || '',
    });
    setModalOpen(true);
  }, []);

  // Handle URL-based autofill for new patrol (e.g., from Guard Locations page)
  useEffect(() => {
    if (!initialLoadComplete || searchParams.get('action') !== 'newPatrol') return;

    const autofill = {
      ContactName: searchParams.get('contactName') || '',
      CustomerName: searchParams.get('customerName') || '',
      AddressName: searchParams.get('addressName') || '',
    };

    // Clear URL params to prevent re-triggering on refresh
    setSearchParams({}, { replace: true });

    // Open the patrol modal with autofill data
    handleAddNewPatrol(autofill);
  }, [initialLoadComplete, searchParams, setSearchParams, handleAddNewPatrol]);

  // Handle closing modal
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedPatrol(null);
  }, []);

  // Track pending shift selection after patrol updates
  const pendingShiftSelectionRef = useRef(null);

  // Effect to handle shift selection after ShiftView patrols update
  useEffect(() => {
    if (pendingShiftSelectionRef.current) {
      const patrolData = pendingShiftSelectionRef.current;
      const shift = findShiftForPatrol(shiftViewShifts, patrolData);
      if (shift) {
        setSelectedShift(shift);
      }
      pendingShiftSelectionRef.current = null;
    }
  }, [shiftViewShifts]);

  /**
   * Helper function to refresh Calendar patrols from the API.
   */
  const refreshCalendarPatrols = useCallback(async () => {
    if (!calendarWeekStart) return;

    const weekEnd = getWeekEnd(calendarWeekStart);

    try {
      let patrolsData = [];
      if (viewMode === 'guard' && selectedGuardId) {
        patrolsData = await loadScheduleByContactRef.current(selectedGuardId, calendarWeekStart, weekEnd);
      } else if (viewMode === 'company' && selectedCompanyId) {
        patrolsData = await loadScheduleByCompanyRef.current(
          selectedCompanyId,
          calendarWeekStart,
          weekEnd,
          selectedLocationId || null
        );
      }
      setCalendarPatrols(patrolsData);
    } catch (error) {
      console.error('[Scheduler] Failed to refresh Calendar patrols:', error);
    }
  }, [calendarWeekStart, viewMode, selectedGuardId, selectedCompanyId, selectedLocationId]);

  /**
   * Helper function to refresh ShiftView patrols from the API.
   */
  const refreshShiftViewPatrols = useCallback(async () => {
    if (!shiftViewWeekStart || !shiftViewGuardId) return;

    const weekEnd = getWeekEnd(shiftViewWeekStart);

    try {
      const patrolsData = await loadScheduleByContactRef.current(shiftViewGuardId, shiftViewWeekStart, weekEnd);
      setShiftViewPatrols(patrolsData);
    } catch (error) {
      console.error('[Scheduler] Failed to refresh ShiftView patrols:', error);
    }
  }, [shiftViewWeekStart, shiftViewGuardId]);

  /**
   * Helper function to refresh both Calendar and ShiftView patrols.
   * Used after database writes to ensure both views are in sync.
   */
  const refreshBothViews = useCallback(async () => {
    await Promise.all([
      refreshCalendarPatrols(),
      refreshShiftViewPatrols(),
    ]);
  }, [refreshCalendarPatrols, refreshShiftViewPatrols]);

  // Handle saving patrol (create or edit)
  // This is a SYNC POINT: database writes update both views
  const handleSavePatrol = useCallback(
    async (patrolData, rescheduledPatrols = []) => {
      try {
        // Save the patrol — pass rescheduled patrols so savePatrol can include
        // their updated times in the full-replacement API call
        await savePatrol(patrolData, calendarPatrols, rescheduledPatrols);

        // Update Calendar to show the saved patrol
        const patrolWeekStart = getWeekStart(new Date(patrolData.StartTime));
        setCalendarWeekStart(patrolWeekStart);

        if (viewMode === 'company') {
          const companyChanged = selectedCompanyId !== patrolData.CustomerId;
          const locationChanged = selectedLocationId && selectedLocationId !== patrolData.AddressId;
          setSelectedCompanyId(patrolData.CustomerId);
          setSelectedCompany(patrolData.CustomerName);
          // Only update location filter if one was already selected
          if (selectedLocationId) {
            setSelectedLocationId(patrolData.AddressId);
            setSelectedLocation(patrolData.AddressName);
          }
          if (companyChanged || locationChanged) {
            const label = selectedLocationId
              ? `${patrolData.CustomerName} — ${patrolData.AddressName}`
              : patrolData.CustomerName;
            notifyInfo(`Calendar filtered to ${label}`);
          }
        } else {
          const guardChanged = selectedGuardId !== patrolData.ContactId;
          setSelectedGuardId(patrolData.ContactId);
          setSelectedGuard(patrolData.ContactName);
          if (guardChanged) {
            notifyInfo(`Calendar filtered to ${patrolData.ContactName}`);
          }
        }

        // Navigate Calendar to show the saved patrol
        if (calendarNavigateRef.current) {
          calendarNavigateRef.current(patrolData.StartTime);
        }

        // Update ShiftView to show the saved patrol
        setShiftViewGuardId(patrolData.ContactId);
        setShiftViewGuard(patrolData.ContactName);
        setShiftViewWeekStart(patrolWeekStart);

        // Refresh both views from the API to get the latest data
        await refreshBothViews();

        // Schedule shift selection for after state update
        pendingShiftSelectionRef.current = patrolData;
      } catch (error) {
        // Error already handled and notified in useSchedulerApi
        console.error('[Scheduler] Save patrol failed:', error);
      }
    },
    [viewMode, selectedCompanyId, selectedLocationId, selectedGuardId, notifyInfo, savePatrol, calendarPatrols, refreshBothViews]
  );

  // Handle deleting patrol
  // This is a SYNC POINT: database writes update both views
  const handleDeletePatrol = useCallback(
    async (scheduleAssignmentId) => {
      // Find the patrol to delete from Calendar or ShiftView patrols
      const patrolToDelete = calendarPatrols.find((p) => p.ScheduleAssignmentId === scheduleAssignmentId)
        || shiftViewPatrols.find((p) => p.ScheduleAssignmentId === scheduleAssignmentId);

      if (!patrolToDelete) {
        console.error('[Scheduler] Patrol not found for deletion:', scheduleAssignmentId);
        return;
      }

      try {
        // Call the API to delete the patrol
        await deletePatrol(patrolToDelete);

        // Refresh both views from the API to get the latest data
        await refreshBothViews();

        // Clear selected shift if the deleted patrol was part of it
        setSelectedShift((currentShift) => {
          if (currentShift?.patrols.some((p) => p.ScheduleAssignmentId === scheduleAssignmentId)) {
            return null;
          }
          return currentShift;
        });
      } catch (error) {
        // Error already handled and notified in useSchedulerApi
        console.error('[Scheduler] Delete patrol failed:', error);
      }
    },
    [calendarPatrols, shiftViewPatrols, deletePatrol, refreshBothViews]
  );

  // Handle shift selection from ShiftView browser
  // Note: This does NOT sync to Calendar - views are independent
  const handleShiftSelect = useCallback((selectedShiftData) => {
    setSelectedShift(selectedShiftData);
  }, []);

  // Handle reset/clear shift selection in ShiftView
  const handleShiftReset = useCallback(() => {
    setSelectedShift(null);
  }, []);

  // Handle reordering patrols within a shift
  // This is a SYNC POINT: database writes update both views
  const handleReorderPatrols = useCallback(
    async (updatedPatrols) => {
      if (!updatedPatrols || updatedPatrols.length === 0) return;

      try {
        // Update each reordered patrol's times individually via the V4 API
        const updatePromises = updatedPatrols
          .filter((p) => p.ScheduleAssignmentId)
          .map((p) =>
            updateSinglePatrol(p.ScheduleAssignmentId, {
              StartTime: p.StartTime,
              EndTime: p.EndTime,
            })
          );

        await Promise.all(updatePromises);
        notifySuccess('Patrol order saved');

        // Update ShiftView local state optimistically for immediate feedback
        setShiftViewPatrols((prevPatrols) => {
          const updatedMap = new Map(updatedPatrols.map((p) => [p.ScheduleAssignmentId, p]));
          return prevPatrols.map((patrol) => {
            if (updatedMap.has(patrol.ScheduleAssignmentId)) {
              return updatedMap.get(patrol.ScheduleAssignmentId);
            }
            return patrol;
          });
        });

        // Also update Calendar patrols if they contain these patrols
        setCalendarPatrols((prevPatrols) => {
          const updatedMap = new Map(updatedPatrols.map((p) => [p.ScheduleAssignmentId, p]));
          return prevPatrols.map((patrol) => {
            if (updatedMap.has(patrol.ScheduleAssignmentId)) {
              return updatedMap.get(patrol.ScheduleAssignmentId);
            }
            return patrol;
          });
        });
      } catch (error) {
        console.error('[Scheduler] Reorder patrols failed:', error);
        notifyError('Failed to save patrol order. Please try again.');
        // Refresh both views to restore original state on error
        await refreshBothViews();
      }
    },
    [refreshBothViews, notifySuccess, notifyError]
  );

  // Execute bulk save operation (called after conflict warning is confirmed)
  // This is a SYNC POINT: database writes update both views
  const executeBulkSave = useCallback(
    async (duplicatedPatrols, operationType) => {
      try {
        // Call the API to save the duplicated patrols
        await bulkSavePatrols(duplicatedPatrols, calendarPatrols);

        const firstPatrol = duplicatedPatrols[0];
        if (firstPatrol) {
          const patrolWeekStart = getWeekStart(new Date(firstPatrol.StartTime));

          // Update Calendar to show the duplicated patrols
          setCalendarWeekStart(patrolWeekStart);
          if (viewMode === 'company') {
            const companyChanged = selectedCompanyId !== firstPatrol.CustomerId;
            const locationChanged = selectedLocationId && selectedLocationId !== firstPatrol.AddressId;
            setSelectedCompanyId(firstPatrol.CustomerId);
            setSelectedCompany(firstPatrol.CustomerName);
            // Only update location filter if one was already selected
            if (selectedLocationId) {
              setSelectedLocationId(firstPatrol.AddressId);
              setSelectedLocation(firstPatrol.AddressName);
            }
            if (companyChanged || locationChanged) {
              const label = selectedLocationId
                ? `${firstPatrol.CustomerName} — ${firstPatrol.AddressName}`
                : firstPatrol.CustomerName;
              notifyInfo(`Calendar filtered to ${label}`);
            }
          } else {
            const guardChanged = selectedGuardId !== firstPatrol.ContactId;
            setSelectedGuardId(firstPatrol.ContactId);
            setSelectedGuard(firstPatrol.ContactName);
            if (guardChanged) {
              notifyInfo(`Calendar filtered to ${firstPatrol.ContactName}`);
            }
          }

          // Navigate Calendar to show the duplicated patrols
          if (calendarNavigateRef.current) {
            calendarNavigateRef.current(firstPatrol.StartTime);
          }

          // Update ShiftView to show the duplicated patrols
          setShiftViewGuardId(firstPatrol.ContactId);
          setShiftViewGuard(firstPatrol.ContactName);
          setShiftViewWeekStart(patrolWeekStart);

          // Schedule shift selection for after state update (for shift duplication)
          if (operationType === 'shift') {
            pendingShiftSelectionRef.current = firstPatrol;
          }
        }

        // Refresh both views from the API to get the latest data
        await refreshBothViews();
      } catch (error) {
        // Error already handled and notified in useSchedulerApi
        console.error(`[Scheduler] Duplicate ${operationType} failed:`, error);
      }
    },
    [viewMode, selectedCompanyId, selectedLocationId, selectedGuardId, notifyInfo, bulkSavePatrols, calendarPatrols, refreshBothViews]
  );

  // Handle confirming bulk conflict warning
  const handleConfirmBulkConflict = useCallback(async () => {
    if (pendingBulkOperation) {
      setShowBulkConflictWarning(false);
      setBulkConflictSummary(null);
      await executeBulkSave(pendingBulkOperation.patrols, pendingBulkOperation.type);
      setPendingBulkOperation(null);
    }
  }, [pendingBulkOperation, executeBulkSave]);

  // Handle canceling bulk conflict warning
  const handleCancelBulkConflict = useCallback(() => {
    setShowBulkConflictWarning(false);
    setBulkConflictSummary(null);
    setPendingBulkOperation(null);
  }, []);

  // Handle duplicating an entire shift to the next day
  const handleDuplicateShift = useCallback(
    async (duplicatedPatrols) => {
      if (!duplicatedPatrols || duplicatedPatrols.length === 0) return;

      // Check for conflicts before saving (check against both views' patrols)
      const allPatrols = [...calendarPatrols, ...shiftViewPatrols];
      const uniquePatrols = Array.from(
        new Map(allPatrols.map((p) => [p.ScheduleAssignmentId, p])).values()
      );
      const conflicts = checkBulkConflicts(duplicatedPatrols, uniquePatrols);
      if (conflicts && conflicts.totalCount > 0) {
        setPendingBulkOperation({ patrols: duplicatedPatrols, type: 'shift' });
        setBulkConflictSummary(conflicts);
        setShowBulkConflictWarning(true);
        return;
      }

      await executeBulkSave(duplicatedPatrols, 'shift');
    },
    [checkBulkConflicts, calendarPatrols, shiftViewPatrols, executeBulkSave]
  );

  // Handle duplicating all visible patrols from current week to next week
  const handleDuplicateWeek = useCallback(
    async (duplicatedPatrols) => {
      if (!duplicatedPatrols || duplicatedPatrols.length === 0) return;

      // Check for conflicts before saving
      const conflicts = checkBulkConflicts(duplicatedPatrols, calendarPatrols);
      if (conflicts && conflicts.totalCount > 0) {
        setPendingBulkOperation({ patrols: duplicatedPatrols, type: 'week' });
        setBulkConflictSummary(conflicts);
        setShowBulkConflictWarning(true);
        return;
      }

      await executeBulkSave(duplicatedPatrols, 'week');
    },
    [checkBulkConflicts, calendarPatrols, executeBulkSave]
  );

  // Handle deleting an entire shift (all patrols within it)
  // This is a SYNC POINT: database writes update both views
  const handleDeleteShift = useCallback(
    async (shiftPatrols) => {
      if (!shiftPatrols || shiftPatrols.length === 0) return;

      try {
        // Combine patrols from both views for the API
        const allPatrols = [...calendarPatrols, ...shiftViewPatrols];
        const uniquePatrols = Array.from(
          new Map(allPatrols.map((p) => [p.ScheduleAssignmentId, p])).values()
        );

        // Call the API to delete all patrols in the shift
        await deleteShift(shiftPatrols, uniquePatrols);

        // Clear the selected shift since it no longer exists
        setSelectedShift(null);

        // Refresh both views from the API to get the latest data
        await refreshBothViews();
      } catch (error) {
        // Error already handled and notified in useSchedulerApi
        console.error('[Scheduler] Delete shift failed:', error);
      }
    },
    [deleteShift, calendarPatrols, shiftViewPatrols, refreshBothViews]
  );

  // Handle changing the guard for an entire shift
  // This is a SYNC POINT: database writes update both views
  const handleChangeShiftGuard = useCallback(
    async (shiftPatrols, newGuard) => {
      if (!shiftPatrols || shiftPatrols.length === 0 || !newGuard) return;

      try {
        // Combine patrols from both views for the API
        const allPatrols = [...calendarPatrols, ...shiftViewPatrols];
        const uniquePatrols = Array.from(
          new Map(allPatrols.map((p) => [p.ScheduleAssignmentId, p])).values()
        );

        // Call the API to change the guard for all patrols
        await changeShiftGuard(shiftPatrols, newGuard, uniquePatrols);

        // Clear the selected shift (it will be recreated with new guard)
        setSelectedShift(null);

        // Update Calendar to show the new guard's patrols
        if (viewMode === 'guard') {
          const guardChanged = selectedGuardId !== newGuard.id;
          setSelectedGuardId(newGuard.id);
          setSelectedGuard(newGuard.name);
          if (guardChanged) {
            notifyInfo(`Calendar filtered to ${newGuard.name}`);
          }
        }

        // Update ShiftView to show the new guard's patrols
        setShiftViewGuardId(newGuard.id);
        setShiftViewGuard(newGuard.name);

        // Refresh both views from the API to get the latest data
        await refreshBothViews();
      } catch (error) {
        // Error already handled and notified in useSchedulerApi
        console.error('[Scheduler] Change shift guard failed:', error);
      }
    },
    [changeShiftGuard, calendarPatrols, shiftViewPatrols, refreshBothViews, viewMode, selectedGuardId, notifyInfo]
  );

  // Register calendar navigation function
  const registerCalendarNavigate = useCallback((navigateFunction) => {
    calendarNavigateRef.current = navigateFunction;
  }, []);

  // =====================
  // EXPORT FUNCTIONALITY
  // =====================

  // Handle opening export modal
  const handleExportClick = useCallback(() => {
    setShowExportModal(true);
  }, []);

  // Handle closing export modal
  const handleExportClose = useCallback(() => {
    setShowExportModal(false);
  }, []);

  return (
    <Box sx={{ width: '100%', padding: { xs: 1, sm: 2 } }}>
      {/* Top Row: Control Bar + AI Insights */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          marginBottom: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Control Bar */}
        <Box sx={{ flex: 3 }}>
          <ControlBar
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onCompanyChange={handleCompanyChange}
            onLocationChange={handleLocationChange}
            selectedCompany={selectedCompany}
            selectedLocation={selectedLocation}
            selectedGuardId={selectedGuardId}
            onGuardChange={handleGuardChange}
            guards={guards}
            companies={companies}
            onAddNewPatrol={handleAddNewPatrol}
            onExport={handleExportClick}
            isLoadingSchedule={loadingState.patrols}
            isMobile={isMobile}
          />
        </Box>

        {/* AI Insights Panel - Hidden on mobile */}
        {!isMobile && (
          <Paper
            elevation={2}
            sx={{
              p: 2,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '4px solid',
              borderLeftColor: 'primary.main',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              AI Insights
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.disabled',
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}
              >
                AI Insights coming soon
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>

      {!initialLoadComplete || loadingState.initial ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            gap: 2,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading scheduler...
          </Typography>
        </Box>
      ) : (
        /* Responsive Layout: Single column on mobile, two columns on desktop */
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Calendar - Full width on mobile, 3/4 on desktop */}
          <Paper
            elevation={2}
            sx={{
              flex: { xs: 'none', md: '3' },
              width: { xs: '100%', md: 'auto' },
              padding: 0,
            }}
          >
            <SchedulerCalendar
              patrols={calendarPatrols}
              shifts={calendarShifts}
              viewMode={viewMode}
              selectedCompanyId={selectedCompanyId}
              selectedLocationId={selectedLocationId}
              selectedGuardId={selectedGuardId}
              selectedCompanyName={selectedCompany}
              selectedLocationName={selectedLocation}
              selectedGuardName={selectedGuard}
              onEventClick={handleCalendarEventClick}
              onEditPatrol={handlePatrolClick}
              onRegisterNavigate={registerCalendarNavigate}
              onDuplicateWeek={handleDuplicateWeek}
              onWeekChange={handleCalendarWeekChange}
              isMobile={isMobile}
            />
          </Paper>

          {/* ShiftView - Hidden on mobile (shown in drawer), visible on tablet/desktop */}
          {!isMobile && (
            <Paper
              elevation={2}
              sx={{
                flex: '1',
                padding: 2,
              }}
            >
              <ShiftView
                shift={selectedShift}
                onPatrolClick={handlePatrolClick}
                onShiftSelect={handleShiftSelect}
                onReset={handleShiftReset}
                onAddPatrol={handleAddNewPatrol}
                onDuplicateShift={handleDuplicateShift}
                onReorderPatrols={handleReorderPatrols}
                onDeleteShift={handleDeleteShift}
                onChangeShiftGuard={handleChangeShiftGuard}
                onBrowseGuardChange={handleShiftViewGuardChange}
                onBrowseWeekChange={handleShiftViewWeekChange}
                onSyncToCalendar={handleSyncToCalendar}
                guards={guards}
                allShifts={shiftViewShifts}
                browseGuardId={shiftViewGuardId}
                browseGuardName={shiftViewGuard}
                browseWeekStart={shiftViewWeekStart}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* Mobile FAB to open ShiftView drawer */}
      {isMobile && initialLoadComplete && !loadingState.initial && (
        <Fab
          color="primary"
          aria-label="Open shift view"
          onClick={() => setMobileDrawerOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <CalendarViewDayIcon />
        </Fab>
      )}

      {/* Mobile ShiftView Drawer */}
      {isMobile && (
        <MobileShiftDrawer
          open={mobileDrawerOpen}
          onOpen={() => setMobileDrawerOpen(true)}
          onClose={() => setMobileDrawerOpen(false)}
        >
          <ShiftView
            shift={selectedShift}
            onPatrolClick={handlePatrolClick}
            onShiftSelect={handleShiftSelect}
            onReset={handleShiftReset}
            onAddPatrol={handleAddNewPatrol}
            onDuplicateShift={handleDuplicateShift}
            onReorderPatrols={handleReorderPatrols}
            onDeleteShift={handleDeleteShift}
            onChangeShiftGuard={handleChangeShiftGuard}
            onBrowseGuardChange={handleShiftViewGuardChange}
            onBrowseWeekChange={handleShiftViewWeekChange}
            onSyncToCalendar={handleSyncToCalendar}
            guards={guards}
            allShifts={shiftViewShifts}
            browseGuard={shiftViewGuard}
            browseWeekStart={shiftViewWeekStart}
            isMobile={isMobile}
          />
        </MobileShiftDrawer>
      )}

      {/* Patrol Modal */}
      <PatrolModal
        open={modalOpen}
        onClose={handleModalClose}
        patrol={selectedPatrol}
        onSave={handleSavePatrol}
        onDelete={handleDeletePatrol}
        autofillCustomerName={autofillData.CustomerName}
        autofillAddressName={autofillData.AddressName}
        autofillContactId={autofillData.ContactId}
        autofillContactName={autofillData.ContactName}
        autofillDate={autofillData.date}
        autofillStartTime={autofillData.startTime}
        autofillDuration={autofillData.duration}
        currentShift={selectedShift}
        existingPatrols={calendarPatrols}
      />

      {/* Saving/Deleting Overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
        open={loadingState.saving || loadingState.deleting}
      >
        <Fade in={loadingState.saving || loadingState.deleting}>
          <Paper
            elevation={4}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={48} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SaveIcon color="primary" />
              <Typography variant="h6" color="text.primary">
                {loadingState.deleting ? 'Deleting...' : 'Saving...'}
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Backdrop>

      {/* Bulk Conflict Warning Dialog */}
      <Dialog
        open={showBulkConflictWarning}
        onClose={handleCancelBulkConflict}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorOutlineIcon sx={{ color: 'error.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Scheduling Conflicts Detected
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {bulkConflictSummary?.totalCount === 1
              ? 'This operation will create a scheduling conflict:'
              : `This operation will create ${bulkConflictSummary?.totalCount} scheduling conflicts:`}
          </Typography>
          {bulkConflictSummary && bulkConflictSummary.conflicts && (
            <List
              dense
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                py: 0,
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {bulkConflictSummary.conflicts.slice(0, 5).map((conflict, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={conflict.message}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                  />
                </ListItem>
              ))}
              {bulkConflictSummary.conflicts.length > 5 && (
                <ListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={`... and ${bulkConflictSummary.conflicts.length - 5} more conflicts`}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontStyle: 'italic',
                      color: 'text.secondary',
                    }}
                  />
                </ListItem>
              )}
            </List>
          )}
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            Do you want to proceed anyway? This may result in guards being double-booked.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelBulkConflict} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmBulkConflict} color="error" variant="contained">
            {pendingBulkOperation?.type === 'week' ? 'Duplicate Week Anyway' : 'Duplicate Anyway'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Modal */}
      <ExportModal
        open={showExportModal}
        onClose={handleExportClose}
        guards={guards}
        companies={companies}
        defaultCompanyId={viewMode === 'company' ? selectedCompanyId : null}
        defaultLocationId={viewMode === 'company' ? selectedLocationId : null}
        defaultGuardId={viewMode === 'guard' ? selectedGuardId : null}
        defaultStartDate={calendarWeekStart}
        defaultEndDate={calendarWeekStart ? getWeekEnd(calendarWeekStart) : null}
        viewMode={viewMode}
      />
    </Box>
  );
};

export default Scheduler;
